/**
 * Splits a field value into tokens, marking the ones the scan matched.
 *
 * Returns data rather than markup on purpose: the matched word has to be visibly
 * marked inside text that came from the database, and `v-html` on network data is the
 * one thing the security rule forbids outright. The template renders these tokens
 * through `{{ }}`, so a client named `<script>` stays a client named `<script>`.
 *
 * The scan sends back the words from the *query* that matched, not the words in the
 * value, and it matches tolerantly — "shenker" is returned for a value reading
 * "SCHENKER ITALIA S.R.L.". So comparing for equality highlights nothing on exactly
 * the near-miss results the feature exists to surface. The comparison below mirrors
 * the scan's rule (case- and accent-folded, then a length-banded edit distance) close
 * enough to decide emphasis. The authoritative rule lives server-side; if the two ever
 * disagree the cost is a word emphasised or not, never a result shown or hidden.
 */

export interface HighlightToken {
  text: string;
  matched: boolean;
}

/** Same folding the scan's collation applies: case- and accent-insensitive, symbols out. */
function normalise(word: string): string {
  return word
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

/**
 * The scan's length bands (BR-008): a short word must match exactly, a long one may slip
 * twice. The two-edit band starts at 8 — at 7, two edits reached "spencer" and "speaker"
 * from "shenker" in production. Kept in step with the server, or this marks words in a
 * row that the scan itself would not have matched.
 */
function tolerance(length: number): number {
  if (length <= 3) return 0;
  if (length <= 7) return 1;
  return 2;
}

/** Optimal string alignment, bounded — anything past the band is not worth measuring. */
function editDistanceWithin(a: string, b: string, max: number): boolean {
  if (a === b) return true;
  if (max === 0) return false;
  if (Math.abs(a.length - b.length) > max) return false;

  let previous = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i += 1) {
    const current = [i];
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      current[j] = Math.min(
        previous[j] + 1,
        current[j - 1] + 1,
        previous[j - 1] + cost,
      );
    }
    previous = current;
  }
  return previous[b.length] <= max;
}

function isMatch(queryWord: string, valueWord: string): boolean {
  if (!queryWord || !valueWord) return false;
  return editDistanceWithin(queryWord, valueWord, tolerance(queryWord.length));
}

// Letters and digits, including accented Latin (Muñoz) and combining marks. \p{L} would
// say this more simply but the build targets ES5, where it is not available.
const WORD_CHARS =
  'A-Za-z0-9\\u00C0-\\u00D6\\u00D8-\\u00F6\\u00F8-\\u024F\\u0300-\\u036F';

/**
 * A word as the scan reads one (BR-001): punctuation is a boundary, so "john@doe.com" is
 * john, doe and com. The exceptions are the scan's too — "&" or "+" between letters and a
 * possessive apostrophe join rather than split, so "CI&T" is the single word "cit".
 * Splitting on whitespace alone glued "john@doe.com" into "johndoecom", which matches
 * nothing: the same punctuation-deletion defect the rebuild exists to remove.
 */
const WORD = new RegExp(`[${WORD_CHARS}]+(?:[&+'\\u2019][${WORD_CHARS}]+)*`, 'g');

export function highlightTokens(
  value: string | null | undefined,
  matchedWords: string[] = [],
): HighlightToken[] {
  if (!value) return [];
  const wanted = matchedWords.map(normalise).filter(Boolean);
  const tokens: HighlightToken[] = [];
  let end = 0;

  // Separators become their own unmarked tokens, so the value renders exactly as stored.
  value.replace(WORD, (word: string, offset: number) => {
    if (offset > end) tokens.push({ text: value.slice(end, offset), matched: false });
    const normalised = normalise(word);
    tokens.push({
      text: word,
      matched: wanted.some((queryWord) => isMatch(queryWord, normalised)),
    });
    end = offset + word.length;
    return word;
  });

  if (end < value.length) tokens.push({ text: value.slice(end), matched: false });
  return tokens;
}

/** Long field values are truncated in a row but never in the preview panel. */
export function truncate(value: string, max = 180): string {
  return value.length > max ? `${value.slice(0, max)}…` : value;
}

export default highlightTokens;
